import React, { useEffect, useState } from 'react';
import type { CriticalLimit } from '../../types';
import { Gauge, ShieldAlert, Plus, Minus, AlertTriangle } from 'lucide-react';

interface GaugeInputProps {
  itemId: string;
  itemNo: number;
  unit?: string;
  value?: number | string;
  minValue?: number;
  maxValue?: number;
  criticalLimit?: CriticalLimit;
  disabled?: boolean;
  onChange: (value: number | undefined, isAbnormal: boolean, warningMessage?: string) => void;
}

export const GaugeInput: React.FC<GaugeInputProps> = ({
  itemId,
  itemNo,
  unit = 'MPa',
  value,
  minValue,
  maxValue,
  criticalLimit,
  disabled = false,
  onChange,
}) => {
  const [inputValue, setInputValue] = useState<string>(
    value !== undefined && value !== null ? String(value) : ''
  );

  // Evaluate tolerance whenever inputValue changes
  useEffect(() => {
    if (value !== undefined && value !== null && String(value) !== inputValue) {
      setInputValue(String(value));
    }
  }, [value]);

  const evaluateValue = (valStr: string) => {
    if (!valStr.trim()) {
      onChange(undefined, false, undefined);
      return;
    }

    const num = parseFloat(valStr);
    if (!Number.isFinite(num)) {
      onChange(undefined, true, 'ค่าที่ป้อนไม่ใช่ตัวเลขที่ถูกต้อง');
      return;
    }

    let isAbnormal = false;
    let warningMsg: string | undefined = undefined;

    // Check non-negative physical constraint for gauge readings
    // (Physical pressure/level gauges must not accept negative readings unless explicitly configured)
    if (num < 0 && (minValue === undefined || minValue >= 0)) {
      isAbnormal = true;
      warningMsg = `ค่า ${num} ${unit} ต้องไม่เป็นค่าติดลบ (เกจวัดทางกายภาพ)`;
    }

    // Check critical limit first (e.g. Item 4: < 6.8 MPa)
    if (!isAbnormal && criticalLimit) {
      const { operator, value: limitVal, warningMessage } = criticalLimit;
      let breached = false;
      if (operator === '<' && num < limitVal) breached = true;
      else if (operator === '<=' && num <= limitVal) breached = true;
      else if (operator === '>' && num > limitVal) breached = true;
      else if (operator === '>=' && num >= limitVal) breached = true;

      if (breached) {
        isAbnormal = true;
        warningMsg =
          warningMessage ||
          `แรงดัน ${num} ${unit} ต่ำกว่าเกณฑ์ความปลอดภัย (${operator} ${limitVal} ${unit})!`;
      }
    }

    // Check min/max bounds if not already flagged
    if (!isAbnormal) {
      if (minValue !== undefined && num < minValue) {
        isAbnormal = true;
        warningMsg = `ค่า ${num} ${unit} ต่ำกว่าเกณฑ์มาตรฐานขั้นต่ำ (${minValue} ${unit})`;
      } else if (maxValue !== undefined && num > maxValue) {
        isAbnormal = true;
        warningMsg = `ค่า ${num} ${unit} สูงกว่าเกณฑ์มาตรฐานสูงสุด (${maxValue} ${unit})`;
      }
    }

    onChange(num, isAbnormal, warningMsg);
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    setInputValue(raw);
    evaluateValue(raw);
  };

  const handleStep = (delta: number) => {
    if (disabled) return;
    const current = parseFloat(inputValue) || 0;
    const nextVal = Math.max(0, +(current + delta).toFixed(2));
    const nextStr = String(nextVal);
    setInputValue(nextStr);
    evaluateValue(nextStr);
  };

  const num = parseFloat(inputValue);
  const isCurrentlyAbnormal =
    !Number.isFinite(num) && inputValue.trim() !== ''
      ? true
      : Number.isFinite(num) &&
        ((num < 0 && (minValue === undefined || minValue >= 0)) ||
          ((criticalLimit &&
            ((criticalLimit.operator === '<' && num < criticalLimit.value) ||
              (criticalLimit.operator === '<=' && num <= criticalLimit.value) ||
              (criticalLimit.operator === '>' && num > criticalLimit.value) ||
              (criticalLimit.operator === '>=' && num >= criticalLimit.value))) ||
            (minValue !== undefined && num < minValue) ||
            (maxValue !== undefined && num > maxValue)));

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label
          htmlFor={`gauge-input-${itemId}`}
          className="text-xs font-bold uppercase tracking-wider text-slate-700 flex items-center gap-1.5"
        >
          <Gauge className="w-4 h-4 text-blue-600" />
          ค่าที่วัดได้จากเกจ (Measured Value)
        </label>
        {minValue !== undefined && (
          <span className="text-[11px] font-mono text-slate-500 font-semibold">
            เกณฑ์: {minValue !== undefined ? `>= ${minValue}` : ''}
            {maxValue !== undefined ? ` - ${maxValue}` : ''} {unit}
          </span>
        )}
      </div>

      {/* Main Touch Input Controls with +/- Ergonomic Steppers */}
      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled={disabled}
          onClick={() => handleStep(-0.1)}
          className="w-12 h-12 rounded-lg bg-slate-100 hover:bg-slate-200 active:bg-slate-300 text-slate-800 flex items-center justify-center font-bold text-lg border border-slate-300 disabled:opacity-50 min-h-[48px] min-w-[48px] select-none active:scale-95 transition-transform shadow-2xs"
          aria-label="Decrease by 0.1"
        >
          <Minus className="w-5 h-5" />
        </button>

        <div className="relative flex-1">
          <input
            id={`gauge-input-${itemId}`}
            type="number"
            step="0.1"
            min="0"
            disabled={disabled}
            value={inputValue}
            onChange={handleTextChange}
            placeholder="ป้อนค่าตัวเลข..."
            className={`w-full bg-white border rounded-lg px-4 py-3 text-xl sm:text-2xl font-mono font-black text-center focus:ring-2 focus:outline-none min-h-[48px] transition-all shadow-xs ${
              isCurrentlyAbnormal
                ? 'border-rose-500 text-rose-600 bg-rose-50 focus:ring-rose-500'
                : inputValue.trim() !== ''
                ? 'border-emerald-500 text-emerald-700 bg-emerald-50/50 focus:ring-emerald-500'
                : 'border-slate-300 text-slate-900 focus:ring-blue-500'
            }`}
          />
          <span className="absolute right-3.5 top-1/2 -translate-y-1/2 text-sm font-bold text-slate-500 pointer-events-none">
            {unit}
          </span>
        </div>

        <button
          type="button"
          disabled={disabled}
          onClick={() => handleStep(0.1)}
          className="w-12 h-12 rounded-lg bg-slate-100 hover:bg-slate-200 active:bg-slate-300 text-slate-800 flex items-center justify-center font-bold text-lg border border-slate-300 disabled:opacity-50 min-h-[48px] min-w-[48px] select-none active:scale-95 transition-transform shadow-2xs"
          aria-label="Increase by 0.1"
        >
          <Plus className="w-5 h-5" />
        </button>
      </div>

      {/* Preset Common Value Shortcuts for Faster Workshop Touch Inputs */}
      {itemNo === 4 && (
        <div className="flex items-center gap-1.5 pt-1">
          <span className="text-[11px] text-slate-500 font-semibold">ค่าอ้างอิงด่วน:</span>
          {[6.0, 6.8, 7.0, 7.5].map((preset) => (
            <button
              key={preset}
              type="button"
              onClick={() => {
                setInputValue(String(preset));
                evaluateValue(String(preset));
              }}
              className={`px-2.5 py-1 text-xs font-mono font-semibold rounded border transition-all ${
                preset < 6.8
                  ? 'bg-rose-50 text-rose-700 border-rose-300 hover:bg-rose-100'
                  : 'bg-slate-100 text-slate-700 border-slate-300 hover:bg-slate-200'
              }`}
            >
              {preset.toFixed(1)}
            </button>
          ))}
        </div>
      )}

      {/* Flashing / Animated Out of Tolerance Warning Banner */}
      {isCurrentlyAbnormal && (
        <div className="bg-rose-50 border-2 border-rose-500 rounded-xl p-3.5 flex items-start gap-3 animate-pulse shadow-sm">
          <ShieldAlert className="w-6 h-6 text-rose-600 shrink-0 mt-0.5 animate-bounce" />
          <div className="space-y-1">
            <h4 className="text-sm font-black text-rose-900 uppercase tracking-wide flex items-center gap-1.5">
              <AlertTriangle className="w-4 h-4 text-rose-600" />
              คำเตือนความปลอดภัย: ค่าแรงดันผิดปกติ (OUT-OF-TOLERANCE)
            </h4>
            <p className="text-xs text-rose-800 leading-relaxed font-medium">
              {criticalLimit?.warningMessage ||
                `ค่า ${inputValue} ${unit} อยู่นอกเกณฑ์ความปลอดภัยมาตรฐาน (ต่ำกว่า ${minValue} ${unit})`}
            </p>
            <div className="text-[11px] font-bold text-rose-800 bg-rose-100 border border-rose-200 px-2 py-1 rounded inline-block">
              ⚠️ ระบบปรับสถานะเป็น [NG] อัตโนมัติ และจะส่ง LINE Alert แจ้งเตือนฉุกเฉิน
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
